import {
  Adresse,
  UserDataUpload,
} from '@codefluegel/wowis-rest-typescript-client';
import { Alert, Box, Snackbar } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { FC, useRef, useState } from 'react';

import GridContainer from '../components/container/GridContainer';

import useMedia from '../hooks/useMedia';
import { useStore } from '../store/context';

import { useKundenApi } from '../ApiClient';
import ThemedButton from '../components/button/ThemedButton';
import PasswordModal from '../components/dialog/PasswordModal';
import { GetFormHandle } from '../components/forms/PersonDataForm';
import DigitalSignature, {
  GetDigitalSignatureHandle,
} from '../views/myData/DigitalSignature';
import InvoiceData from '../views/myData/InvoiceData';
import PaymentData from '../views/myData/PaymentData';
import PersonalData from '../views/myData/PersonalData';
import PhoneData from '../views/myData/PhoneData';
import UpdatePassword from '../views/myData/UpdatePassword';

const MyDataPage: FC = observer(() => {
  const { welcomeStore } = useStore();

  const personFormStateRef = useRef<GetFormHandle>(null);
  const signatureFormStateRef = useRef<GetDigitalSignatureHandle>(null);
  const [showPassowrodDialog, setShowPassowrodDialog] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('Error');
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  // data changed state
  const [dataChanged, setDataChanged] = useState(false);

  const welcomeData = welcomeStore.getWelcomeData();
  const user = welcomeData?.userData;
  const isMobile = useMedia('md');
  const isEbgClient = process.env.REACT_APP_CLIENT?.includes('ebg') || false;

  const kundenApi = useKundenApi();

  const getPersonFormState = () => personFormStateRef.current?.getFormState();
  const getSignatureFormState = () =>
    signatureFormStateRef.current?.getDigitalSignatureState();

  const handleSave = async () => {
    const personData = getPersonFormState();
    const signatureData = getSignatureFormState();

    try {
      const userUpload: UserDataUpload = {};
      userUpload.digitalSignature = signatureData;
      await kundenApi.patchKunde(userUpload);

      if (!isEbgClient) {
        const address: Adresse = {
          plz: personData?.zipCode,
          street: personData?.street,
          city: personData?.location,
          adr_id: user?.zustelladresse?.adr_id ?? undefined,
          bundesland: user?.zustelladresse?.bundesland,
          state: user?.zustelladresse?.state,
        };
        if (user?.zustelladresse?.adr_id) {
          await kundenApi.updateKundenZustelladresse(
            user?.customerId ?? 0,
            address,
          );
          user.zustelladresse.plz = address.plz;
          user.zustelladresse.street = address.street;
          user.zustelladresse.city = address.city;
        } else {
          await kundenApi.addKundenZustelladresse(
            user?.customerId ?? 0,
            address,
          );
          if (user) {
            user.zustelladresse = { ...address };
          }
        }
      }

      setSnackbarMessage('Erledigt!');
      setDataChanged(false);
    } catch (e: any) {
      setSnackbarMessage(e.message);
    }

    setSnackbarOpen(true);
  };

  const containerHeight = 'auto';

  return (
    <Box marginBottom={6}>
      <GridContainer>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            paddingTop: 2,
          }}
        >
          <Box width={1} height={1} maxWidth={1200}>
            {dataChanged && (
              <Box
                sx={{
                  width: 1,
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'flex-end',
                }}
              >
                <Box width={100}>
                  <ThemedButton
                    label="Speichern"
                    onClick={() => setShowPassowrodDialog(true)}
                  />
                </Box>
              </Box>
            )}
            <Box
              display="flex"
              flexDirection={isMobile ? 'column' : 'row'}
              flexWrap="wrap"
            >
              {user && (
                <PersonalData
                  ref={personFormStateRef}
                  user={user}
                  onDataChanged={setDataChanged}
                />
              )}
              <Box
                padding={1}
                paddingTop={{ xs: 0, md: 1 }}
                marginBottom={isMobile ? 2 : 4}
                minHeight={containerHeight}
                flexGrow={1}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: isMobile ? 'flex-start' : 'space-between',
                }}
              >
                <PhoneData user={user} />
              </Box>

              <Box display="flex" flexDirection={isMobile ? 'column' : 'row'}>
                <PaymentData user={user} />
                <InvoiceData user={user} />
              </Box>
              <Box
                sx={{
                  width: 1,
                  display: 'flex',
                  flexDirection: isMobile ? 'column' : 'row',
                }}
              >
                {!process.env.REACT_APP_CLIENT?.includes('ebg') && (
                  <DigitalSignature
                    onDataChanged={setDataChanged}
                    ref={signatureFormStateRef}
                    user={user}
                  />
                )}
                <UpdatePassword />
              </Box>
            </Box>
          </Box>
        </Box>
      </GridContainer>
      <PasswordModal
        open={showPassowrodDialog}
        onClose={() => setShowPassowrodDialog(false)}
        onPasswordPassed={handleSave}
      />
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert severity="error" variant="filled" sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
});

export default MyDataPage;
